/**
 * @lines98/client/lib/session-revoked-banner — плашка «сессия завершена».
 *
 * ЧТО: показывает явную модальную плашку, когда `lib/session-revoked` сообщил,
 *   что текущая авторизованная сессия отозвана (вход в аккаунт с другого
 *   браузера/устройства — ротация общего secret_token_hash). Заменяет прежний
 *   ТИХИЙ kick (который ре-минтил призрачного гостя — инцидент 2026-06-10).
 *
 * ЗАЧЕМ модуль (а не код в экране): плашка — общее поведение над любым экраном
 *   (доска/доска дня/результат/реплей/лидерборд/профиль), монтируется один раз
 *   на уровне app-shell. Модель «ствол → ветки» (CLAUDE.md owner-directive
 *   2026-05-30): переиспользуемый модуль, не копия по экранам.
 *
 * ПЕРЕИСПОЛЬЗОВАНИЕ ПРИМИТИВА: рисуем НЕ свою вёрстку, а тот же примитив
 *   `components/confirm-dialog` (нативный `<dialog>` top-layer, токен-размеры,
 *   a11y/focus-trap/Esc из коробки). Owner: «как плашка после авторизации».
 *   Две кнопки confirm-dialog ровно ложатся на два действия:
 *     - cancelLabel (акцентная/безопасная, оранжевая) = «Войти снова»
 *     - confirmLabel (обычная)                        = «Продолжить гостем»
 *   confirmDialog → Promise<boolean>: false (cancel/Esc/фон) = «Войти снова»,
 *   true = «Продолжить гостем».
 *
 * АНТИ-РЕ-МИНТ (корень инцидента): api-client на revoked-401 НЕ минтит гостя
 *   молча. Здесь, по ЯВНОМУ выбору игрока:
 *     - оба варианта сперва `clear()` мёртвую identity (revoked-токен мёртв);
 *     - «Войти снова» → reload на `/` + one-shot флаг авто-открытия входа
 *       (bootShell → `openLoginFlow()` = та же auth-modal, account-profile-redesign.md
 *       §4.1; без хардкода провайдера);
 *     - «Продолжить гостем» → reload на `/` (boot заминтит свежего анонима ТОЛЬКО
 *       сейчас, по согласию — не фоном до ранкед-сабмита).
 *   Перезагрузка на `/` — чистый рестарт стора/экранов (revoked мог случиться
 *   посреди партии); проще и надёжнее ручного размонтирования.
 *
 * SCOPE: клиентский UX над серверным 401. Серверную auth / анти-чит не трогает.
 *
 * Cross-references:
 *   - lib/session-revoked.ts (источник сигнала — onSessionRevoked).
 *   - components/confirm-dialog.ts (переиспользуемый примитив-окно).
 *   - services/identity.ts (clear — снять мёртвую identity).
 *   - app.ts bootShell (вызывает installSessionRevokedBanner один раз;
 *     consumeOpenLoginFlag — авто-открытие логина после reload).
 */

import { confirmDialog } from '../components/confirm-dialog.js';
import { clear as clearIdentity } from '../services/identity.js';
import { t } from './i18n/index.js';

import {
  markSessionRevokedHandled,
  onSessionRevoked,
} from './session-revoked.js';

/**
 * One-shot sessionStorage-флаг: «после reload авто-открой auth-modal входа».
 * Ставится при выборе «Войти снова»; bootShell читает + гасит через
 * `consumeOpenLoginFlag()` → `openLoginFlow()`. sessionStorage (не localStorage) —
 * флаг живёт ровно до следующей загрузки этой вкладки, не протекает в другие.
 */
const OPEN_LOGIN_FLAG = 'lines98.open-login.v1';

let _installed = false;

/**
 * Подписать app-shell на revoked-сигнал. Идемпотентно — повторный вызов no-op
 * (один слушатель на жизнь страницы). Возвращает unsubscribe (для тестов).
 */
export function installSessionRevokedBanner(): () => void {
  if (_installed) return () => {};
  _installed = true;
  return onSessionRevoked(() => {
    void showSessionRevokedBanner();
  });
}

/**
 * Показать плашку + обработать выбор. Экспортируется для прямого вызова/тестов.
 * Никогда не бросает — выбор всегда завершается reload'ом на чистый `/`.
 */
export async function showSessionRevokedBanner(): Promise<void> {
  let continueAsGuest: boolean;
  try {
    // Плашка строится в момент revoked-сигнала (показа) → t() = актуальный язык.
    continueAsGuest = await confirmDialog({
      title: t('game.session.revokedTitle'),
      message: t('game.session.revokedMsg'),
      // cancel = акцентная/безопасная (оранжевая) — рекомендуемое действие.
      cancelLabel: t('game.session.revokedCancel'),
      confirmLabel: t('game.session.revokedConfirm'),
    });
  } catch {
    // Окно не отрисовалось (экзотика) — трактуем как «войти снова».
    continueAsGuest = false;
  }

  // Мёртвую identity снять в любом случае (revoked-токен невалиден).
  try {
    await clearIdentity();
  } catch {
    // clear best-effort; reload всё равно стартует с чистого boot.
  }

  if (!continueAsGuest) {
    // «Войти снова» — попросить boot открыть auth-modal входа после reload.
    try {
      window.sessionStorage?.setItem(OPEN_LOGIN_FLAG, '1');
    } catch {
      // sessionStorage недоступен — войдёт вручную через рейл, не критично.
    }
  }

  // Снять флаг эпизода ДО reload (на случай отмены reload в тестовой среде).
  markSessionRevokedHandled();

  // Чистый рестарт: revoked мог случиться посреди партии — простой и надёжный
  // путь сбросить стор/экраны и заминтить свежего анонима на boot (по согласию).
  reloadToRoot();
}

/**
 * Прочитать + погасить one-shot флаг «открыть логин». Вызывается bootShell после
 * монтирования rail. true — нужно авто-открыть auth-modal входа (`openLoginFlow`).
 */
export function consumeOpenLoginFlag(): boolean {
  try {
    const v = window.sessionStorage?.getItem(OPEN_LOGIN_FLAG);
    if (v) {
      window.sessionStorage?.removeItem(OPEN_LOGIN_FLAG);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/** Перезагрузка на корень. Вынесено для подмены в тестах. */
function reloadToRoot(): void {
  if (typeof window === 'undefined') return;
  window.location.assign('/');
}

/** Test seam: сброс install-флага между кейсами. */
export function _resetForTests(): void {
  _installed = false;
}
